import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Download, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { apiGetOrderBySession } from "@/lib/api";
import GridBackground from "@/components/primitives/GridBackground";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const BOOT_LOG = [
    "// entitlement verified",
    "// activation key generated",
    "// ecosystem access initialized",
    "// your system is online",
];

/**
 * Cinematic SYSTEM ACTIVATION sequence on mount (~3.5s total):
 * 0.0–0.4s  Grid + particle field fade in
 * 0.4–0.9s  Hex shield wireframe stroke draws in
 * 0.9–1.4s  Hex shield fills with cyan glow + bloom flash
 * 1.4–1.6s  Checkmark snap + bloom
 * 1.6–2.2s  Mono boot log lines type in
 * 2.2–2.8s  Headline "ORDER CONFIRMED" fades up
 * 2.8–3.5s  Activation key panel + CTAs stagger in
 *
 * Hex shield enters a continuous pulse-bloom loop after the initial sequence.
 * /* TODO: optional activation audio cue, post-launch */
export default function OrderConfirmedPage() {
    const [search] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = search.get("session_id");
    const { refresh } = useAuth();

    const [order, setOrder] = useState(null);
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);
    const stopped = useRef(false);

    const fetchOnce = useCallback(async () => {
        try {
            const res = await apiGetOrderBySession(sessionId);
            setOrder(res.order || null);
            setUser(res.user || null);
            if (res.user?.activation_key || !res.order?.pending) {
                stopped.current = true;
                await refresh();
            }
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Failed to fetch order");
            stopped.current = true;
        }
    }, [sessionId, refresh]);

    useEffect(() => {
        if (!sessionId) { setError("Missing session_id"); return; }
        let cancelled = false;
        let attempts = 0;
        let timer;
        const tick = async () => {
            if (cancelled || stopped.current) return;
            await fetchOnce();
            attempts += 1;
            if (!stopped.current && attempts < 15) timer = setTimeout(tick, 2000);
        };
        tick();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [sessionId, fetchOnce]);

    const copyKey = () => {
        if (!user?.activation_key) return;
        navigator.clipboard.writeText(user.activation_key);
        toast.success("Activation key copied");
    };

    const ents = user?.entitlements || {};
    const tier = (ents.tier || "operator").toUpperCase();
    const accessType = ents.access_type || "—";
    const amount = order?.amount_total != null ? `$${(order.amount_total / 100).toFixed(2)}` : "—";
    const activatedAt = new Date().toISOString();

    return (
        <div data-testid="order-confirmed-page" className="relative min-h-screen bg-tca-deep text-tca-ink overflow-hidden">
            <GridBackground fine />
            {/* Cyan particle/glow field */}
            <motion.div
                aria-hidden="true"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(circle at 50% 35%, rgba(0,219,255,0.16), transparent 60%), radial-gradient(circle at 20% 80%, rgba(0,219,255,0.08), transparent 60%)",
                }}
            />
            {/* Spotlight beam from above */}
            <motion.div
                aria-hidden="true"
                initial={{ opacity: 0, scaleY: 0 }}
                animate={{ opacity: 0.45, scaleY: 1 }}
                transition={{ duration: 1.2, delay: 0.4 }}
                style={{
                    transformOrigin: "top center",
                    background: "linear-gradient(180deg, rgba(0,219,255,0.18), transparent 70%)",
                }}
                className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[420px] h-[600px] blur-xl"
            />

            <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 pt-16 sm:pt-20 pb-24 text-center">
                {/* Hex shield */}
                <HexShield />

                {/* Boot log */}
                <BootLog />

                {/* Headline */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 2.2 }}
                    className="mt-8"
                >
                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.36em] text-tca-cyan/85">
                        // Order Confirmed
                    </div>
                    <h1 className="mt-3 font-display text-[44px] sm:text-[64px] leading-[1.0] tracking-[-0.025em]">
                        System Activation <em className="not-italic neon-cyan">Complete</em>.
                    </h1>
                </motion.div>

                {/* Activation panel */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 2.8 }}
                    className="mt-10"
                >
                    {error ? (
                        <div data-testid="order-error" className="text-tca-magenta-accent font-mono-tight text-sm">
                            {String(error)}
                        </div>
                    ) : (
                        <>
                            <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted">
                                Activation Key
                            </div>
                            <div className="mt-3 flex items-center justify-center gap-3 flex-wrap">
                                {user?.activation_key ? (
                                    <>
                                        <code
                                            data-testid="activation-key"
                                            className="font-mono-tight text-xl sm:text-2xl text-tca-cyan-hi tracking-[0.14em]"
                                            style={{ textShadow: "0 0 14px rgba(0,219,255,0.5)" }}
                                        >
                                            {user.activation_key}
                                        </code>
                                        <button onClick={copyKey} data-testid="copy-activation-key" className="inline-flex items-center gap-1.5 font-mono-tight text-[10px] uppercase tracking-[0.22em] text-tca-cyan/85 hover:text-tca-cyan border border-tca-cyan/40 px-3 py-1.5 rounded-full transition-all">
                                            <Copy size={12} /> Copy
                                        </button>
                                    </>
                                ) : (
                                    <span className="text-tca-ink-muted text-sm inline-flex items-center gap-2">
                                        <Loader2 size={14} className="animate-spin" /> Provisioning…
                                    </span>
                                )}
                            </div>

                            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    type="button"
                                    onClick={() => navigate("/portal")}
                                    data-testid="order-portal-btn"
                                    className="
                                        inline-flex items-center justify-center gap-2
                                        font-mono-tight font-semibold uppercase tracking-[0.18em] text-xs
                                        py-3.5 px-7 border border-tca-cyan text-tca-cyan-hi
                                        bg-gradient-to-b from-[rgba(0,219,255,0.30)] to-[rgba(0,219,255,0.10)]
                                        hover:text-white shadow-glow-cyan-xs hover:shadow-glow-cyan-lg
                                        rounded-full transition-all duration-300
                                    "
                                >
                                    Open Your Portal <ExternalLink size={13} />
                                </button>
                                <a
                                    href={`${BACKEND}/api/system/download`}
                                    data-testid="order-download-btn"
                                    className="
                                        inline-flex items-center justify-center gap-2
                                        font-mono-tight font-semibold uppercase tracking-[0.18em] text-xs
                                        py-3.5 px-7 border border-tca-ink-dim text-tca-ink-muted
                                        hover:text-tca-cyan-hi hover:border-tca-cyan/55
                                        rounded-full transition-all duration-300
                                    "
                                >
                                    <Download size={13} /> Download System Package
                                </a>
                            </div>
                        </>
                    )}
                </motion.div>

                {/* Activation status mono strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 3.2 }}
                    className="mt-14 max-w-2xl mx-auto"
                    data-testid="activation-status-strip"
                >
                    <div className="border-t border-tca-cyan/15 pt-5 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 font-mono-tight text-[10px] uppercase tracking-[0.22em] text-left">
                        <div className="text-tca-ink-muted">// ecosystem node</div>
                        <div className="text-tca-cyan-hi">thecreatorsagent.com</div>
                        <div className="text-tca-ink-muted">// status</div>
                        <div className="text-tca-cyan-hi">ONLINE</div>
                        <div className="text-tca-ink-muted">// tier</div>
                        <div className="text-tca-cyan-hi">{tier}</div>
                        <div className="text-tca-ink-muted">// build path</div>
                        <div className="text-tca-cyan-hi">{accessType}</div>
                        <div className="text-tca-ink-muted">// amount</div>
                        <div className="text-tca-cyan-hi">{amount}</div>
                        <div className="text-tca-ink-muted">// activation</div>
                        <div className="text-tca-cyan-hi">{activatedAt.slice(0, 19).replace("T", " ")}Z</div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}

function HexShield() {
    // Hex SVG path
    const hexPath = "M 70 4 L 130 38 L 130 110 L 70 144 L 10 110 L 10 38 Z";
    return (
        <div className="relative w-[140px] h-[148px] mx-auto" data-testid="hex-shield">
            <svg width="140" height="148" viewBox="0 0 140 148" className="absolute inset-0">
                {/* Wireframe stroke draw */}
                <motion.path
                    d={hexPath}
                    fill="none"
                    stroke="rgba(0,219,255,0.85)"
                    strokeWidth="1.4"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
                />
                {/* Fill bloom */}
                <motion.path
                    d={hexPath}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.42, 0.22, 0.32] }}
                    transition={{ duration: 0.5, delay: 0.9, times: [0, 0.5, 0.75, 1] }}
                    fill="url(#hexGlow)"
                />
                <defs>
                    <radialGradient id="hexGlow" cx="50%" cy="50%" r="70%">
                        <stop offset="0%" stopColor="#00DBFF" stopOpacity="0.85" />
                        <stop offset="60%" stopColor="#00DBFF" stopOpacity="0.18" />
                        <stop offset="100%" stopColor="#00DBFF" stopOpacity="0" />
                    </radialGradient>
                </defs>
            </svg>
            {/* Continuous pulse-bloom loop after init */}
            <motion.div
                aria-hidden="true"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.32, 0], scale: [0.92, 1.18, 1.18] }}
                transition={{ duration: 4, delay: 2.6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: "radial-gradient(circle, rgba(0,219,255,0.4), transparent 70%)",
                    borderRadius: "50%",
                }}
            />
            {/* Checkmark snap */}
            <motion.div
                initial={{ opacity: 0, scale: 0.4 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.18, delay: 1.4, ease: [0.2, 1.4, 0.4, 1] }}
                className="absolute inset-0 flex items-center justify-center"
            >
                <motion.span
                    aria-hidden="true"
                    animate={{ filter: ["drop-shadow(0 0 0px rgba(0,219,255,0))", "drop-shadow(0 0 18px rgba(0,219,255,0.85))", "drop-shadow(0 0 8px rgba(0,219,255,0.4))"] }}
                    transition={{ duration: 0.45, delay: 1.45 }}
                    className="text-tca-cyan-hi font-bold text-4xl"
                >
                    ✓
                </motion.span>
            </motion.div>
        </div>
    );
}

function BootLog() {
    return (
        <div className="mt-10 mx-auto max-w-md text-left font-mono-tight text-[11px] uppercase tracking-[0.22em]" data-testid="boot-log">
            <AnimatePresence>
                {BOOT_LOG.map((line, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.18, delay: 1.6 + i * 0.15 }}
                        className="text-tca-cyan/85"
                    >
                        {line}
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
}
